export type ErrorCode =
  | "BAD_REQUEST_400"
  | "AUTH_401"
  | "FORBIDDEN_403"
  | "NOT_FOUND_404"
  | "CONFLICT_409"
  | "DB_INTEGRITY_409"
  | "VALIDATION_422"
  | "RATE_LIMIT_429"
  | "SERVER_500"
  | "DB_ERROR_500";

export interface AppError {
  code: ErrorCode | string;
  message: string;
  status: number;
  detail?: string;
}

type ErrorResponseLike = {
  code?: unknown;
  message?: unknown;
  status?: unknown;
  detail?: unknown;
};

export function isErrorResponseLike(
  value: unknown,
): value is ErrorResponseLike {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    ("code" in v && typeof v.code === "string") ||
    ("message" in v && typeof v.message === "string") ||
    ("status" in v && typeof v.status === "number") ||
    ("detail" in v && typeof v.detail === "string")
  );
}

export function isAppError(value: unknown): value is AppError {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.code === "string" &&
    typeof v.message === "string" &&
    typeof v.status === "number"
  );
}

export function mapStatusToCode(status: number): ErrorCode {
  switch (status) {
    case 400:
      return "BAD_REQUEST_400";
    case 401:
      return "AUTH_401";
    case 403:
      return "FORBIDDEN_403";
    case 404:
      return "NOT_FOUND_404";
    case 409:
      return "CONFLICT_409";
    case 422:
      return "VALIDATION_422";
    case 429:
      return "RATE_LIMIT_429";
    case 500:
      return "SERVER_500";
    default:
      if (status >= 500) return "SERVER_500";
      if (status >= 400) return "BAD_REQUEST_400";
      return "SERVER_500";
  }
}

export function toAppError(
  source: unknown,
  fallbackStatus: number = 500,
): AppError {
  try {
    // Already an AppError
    if (isAppError(source)) {
      return source;
    }

    // Backend ErrorResponse-like
    if (isErrorResponseLike(source)) {
      const s = source as ErrorResponseLike;
      const status = typeof s.status === "number" ? s.status : fallbackStatus;
      const code =
        typeof s.code === "string" ? s.code : mapStatusToCode(status);
      const message =
        typeof s.message === "string"
          ? s.message
          : typeof s.detail === "string"
            ? s.detail
            : "A apărut o eroare.";
      const detail = typeof s.detail === "string" ? s.detail : undefined;
      return { code, message, status, detail };
    }

    // Error instance
    if (source instanceof Error) {
      const withStatus = source as Error & {
        status?: number;
        code?: unknown;
        detail?: unknown;
      };
      const status =
        typeof withStatus.status === "number"
          ? withStatus.status
          : fallbackStatus;
      const code =
        typeof withStatus.code === "string"
          ? (withStatus.code as string)
          : mapStatusToCode(status);
      const detail =
        typeof withStatus.detail === "string"
          ? (withStatus.detail as string)
          : undefined;
      return {
        code,
        message: source.message || "A apărut o eroare.",
        status,
        detail,
      };
    }

    // Try to parse stringified JSON error
    if (typeof source === "string") {
      try {
        const parsed = JSON.parse(source);
        if (isErrorResponseLike(parsed)) {
          return toAppError(parsed, fallbackStatus);
        }
      } catch {
        // Not JSON; fall back to generic
      }
      return {
        code: mapStatusToCode(fallbackStatus),
        message: source,
        status: fallbackStatus,
      };
    }

    // Unknown object; best-effort extraction
    if (source && typeof source === "object") {
      const anyObj = source as Record<string, unknown>;
      const status =
        typeof anyObj.status === "number"
          ? (anyObj.status as number)
          : fallbackStatus;
      const message =
        typeof anyObj.message === "string"
          ? (anyObj.message as string)
          : "A apărut o eroare.";
      const code =
        typeof anyObj.code === "string"
          ? (anyObj.code as string)
          : mapStatusToCode(status);
      const detail =
        typeof anyObj.detail === "string"
          ? (anyObj.detail as string)
          : undefined;
      return { code, message, status, detail };
    }

    // Final fallback
    return {
      code: mapStatusToCode(fallbackStatus),
      message: "A apărut o eroare.",
      status: fallbackStatus,
    };
  } catch {
    return {
      code: mapStatusToCode(fallbackStatus),
      message: "A apărut o eroare.",
      status: fallbackStatus,
    };
  }
}
